import React, { useEffect, useState } from 'react'
import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import {
    Button,
    Card,
    Col,
    Drawer,
    Input,
    Row,
    Space,
    Typography,
    message,
} from 'antd'

import {
    Temple,
    TemplePayload,
} from '@a4b/api/src/modules/Monetisation/Puja/types/templeTypes'
import { useAppContext } from '@/components/AppContext'
import withDefaultLayout from '@/hoc/WithDefaultLayout'
import { A4B_APPS } from '@/utils/constants'
import TempleForm from './components/TempleForm'
import TempleListTable from './components/TempleListTable'
import { getTemplePayload } from './utils/helper'

const { Title } = Typography

const ManageTemples = () => {
    const { app, networkInstance } = useAppContext()
    const [temples, setTemples] = useState<Temple[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTitle, setSearchTitle] = useState('')
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [editingTemple, setEditingTemple] = useState<Temple | null>(null)
    const [formLoading, setFormLoading] = useState(false)

    const monetisationPujaApi =
        app === A4B_APPS.SRIMANDIR
            ? networkInstance.clientWithHeaders.monetisationPujaApi
            : networkInstance.clientWithHeaders.monetisationAstroPujaApi

    const fetchTemples = async (title = '') => {
        setLoading(true)
        try {
            const response = await monetisationPujaApi?.templeApi?.getTemples(
                title,
                true,
            )
            setTemples(response.data.data.temples_list || [])
        } catch (error) {
            console.error(error)
            message.error('Failed to fetch temples')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchTemples()
    }, [])

    const handleSearch = (value: string) => {
        setSearchTitle(value)
        fetchTemples(value)
    }

    const handleEdit = (temple: Temple) => {
        setEditingTemple(temple)
        setIsDrawerOpen(true)
    }

    const handleCreate = () => {
        setEditingTemple(null)
        setIsDrawerOpen(true)
    }

    const handleToggleActive = async (temple: Temple, active: boolean) => {
        try {
            const payload = getTemplePayload(temple, active)

            await monetisationPujaApi?.templeApi?.updateTemple(temple.id, payload)
            message.success(
                `Temple ${active ? 'activated' : 'deactivated'} successfully`,
            )
            fetchTemples(searchTitle)
        } catch (error) {
            console.error(error)
            message.error('Failed to update temple status')
        }
    }

    const handleFormFinish = async (values: TemplePayload) => {
        setFormLoading(true)
        try {
            if (editingTemple) {
                await monetisationPujaApi?.templeApi?.updateTemple(
                    editingTemple.id,
                    values,
                )
                message.success('Temple updated successfully')
            } else {
                await monetisationPujaApi?.templeApi?.createTemple(values)
                message.success('Temple created successfully')
            }
            setIsDrawerOpen(false)
            setEditingTemple(null)
            fetchTemples(searchTitle)
        } catch (error) {
            console.error(error)
            message.error('Failed to save temple')
        } finally {
            setFormLoading(false)
        }
    }

    const handleDrawerClose = () => {
        setIsDrawerOpen(false)
        setEditingTemple(null)
    }

    return (
        <div>
            <Card>
                <Row
                    gutter={[16, 16]}
                    justify='space-between'
                    align='middle'
                    style={{ marginBottom: 20, width: '100%' }}
                >
                    <Col>
                        <Title level={4}>Manage Temples</Title>
                    </Col>
                    <Col>
                        <Space>
                            <Input.Search
                                placeholder='Search by title'
                                onSearch={handleSearch}
                                style={{ width: 300 }}
                                enterButton={<SearchOutlined />}
                                allowClear
                            />
                            <Button
                                type='primary'
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                            >
                                Create Temple
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <TempleListTable
                    data={temples}
                    loading={loading}
                    onEdit={handleEdit}
                    onToggleActive={handleToggleActive}
                />

                <Drawer
                    title={editingTemple ? 'Edit Temple' : 'Create Temple'}
                    visible={isDrawerOpen}
                    onClose={handleDrawerClose}
                    width={720}
                    destroyOnClose
                >
                    <TempleForm
                        initialValues={editingTemple || undefined}
                        onFinish={handleFormFinish}
                        loading={formLoading}
                    />
                </Drawer>
            </Card>
        </div>
    )
}

export default withDefaultLayout(ManageTemples)
